import {
  Conversation,
  ConversationType,
  Id,
  User,
  UserRole,
} from '@nextforce/api'
import { Divider, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCompaniesContext } from 'company/context'
import { useCompanyPreferences } from 'company/queries/list'
import React, { useCallback, useMemo, useState } from 'react'
import { FlatList } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { FloatingActionButton, Item } from 'shared/components/fab'
import RefreshControl from 'shared/components/refresh_control'
import EmptyComponent from './EmptyComponent'
import ConversationCard from './card'

interface Props {
  conversations: readonly Conversation[] | undefined
  screenType: string
  tabType: string | undefined
  user: User | undefined
  onPressConversation(conversation: Conversation, messageId?: string): void
  onCreateConversation(conversationType: ConversationType): void
  onLongPress(conversation: Conversation): void
  allowCreateTypes: ConversationType[] | undefined
  role: UserRole
  onRefresh: () => Promise<void>
  chatEnabled?: boolean
  onGoToStarRecipients: () => void
  urgentUnreadConversationIds: string[] | undefined
}

export default function ConversationsView({
  conversations,
  user,
  onPressConversation,
  onCreateConversation,
  onLongPress,
  allowCreateTypes,
  screenType,
  tabType,
  role,
  chatEnabled,
  onRefresh,
  onGoToStarRecipients,
  urgentUnreadConversationIds,
}: Props) {
  const styles = useStyles()
  const { activeCompany } = useCompaniesContext()
  const { data: companyPreferences } = useCompanyPreferences(activeCompany?.id)
  const empRecognitionViaGoldStarsEnabled =
    companyPreferences?.enableEmpRecognitionViaGoldStars ?? false

  const floatingActionButtonItems = useMemo(() => {
    const items: Item[] = []

    items.push(
      {
        name: 'sendDirectMessage',
        title: t('sendDirectMessage'),
        icon: 'envelope',
      },
      {
        name: 'createGroup',
        title: t('createGroup'),
        icon: 'users',
      }
    )

    if (
      empRecognitionViaGoldStarsEnabled &&
      (user?.role === 'observer' || user?.role === 'admin')
    ) {
      items.push({
        name: 'starRecipients',
        title: t('awardStar'),
        icon: 'star',
      })
    }
    return items
  }, [empRecognitionViaGoldStarsEnabled, user?.role])

  const onFloatingActionButtonItemPress = useCallback(
    (name: string) => {
      switch (name) {
        case 'sendDirectMessage':
          return onCreateConversation('dm')
        case 'createGroup':
          return onCreateConversation('custom')
        case 'starRecipients':
          if (
            empRecognitionViaGoldStarsEnabled &&
            (user?.role === 'observer' || user?.role === 'admin')
          )
            return onGoToStarRecipients()
      }
    },
    [
      empRecognitionViaGoldStarsEnabled,
      onCreateConversation,
      onGoToStarRecipients,
      user?.role,
    ]
  )

  const [refreshing, setRefreshing] = useState(false)
  const hasConversations = (conversations?.length ?? 0) > 0
  const shouldShowConversationList = hasConversations && !!chatEnabled

  return (
    <SafeAreaView edges={['top']} style={styles.container}>
      {shouldShowConversationList ? (
        <FlatList
          data={conversations}
          keyExtractor={item => item.id + '' + item.status?.recentMessage?.id}
          refreshControl={
            <RefreshControl
              enabled={true}
              refreshing={refreshing}
              onRefresh={async () => {
                setRefreshing(true)
                await onRefresh()
                setRefreshing(false)
              }}
            />
          }
          renderItem={({ item }) => (
            <ConversationCard
              conversation={item}
              user={user}
              onPressConversation={onPressConversation}
              onLongPress={onLongPress}
              urgent={urgentUnreadConversationIds?.includes(item.id)}
            />
          )}
          ItemSeparatorComponent={Divider}
          contentContainerStyle={styles.contentContainer}
        />
      ) : (
        <EmptyComponent screenType={screenType} tabType={tabType} />
      )}
      <GetCreateButton
        items={floatingActionButtonItems}
        onFloatingActionButtonItemPress={onFloatingActionButtonItemPress}
        allowCreateTypes={allowCreateTypes}
        onCreateConversation={onCreateConversation}
        chatEnabled={chatEnabled}
        companyId={activeCompany?.id}
      />
    </SafeAreaView>
  )
}

interface GetCreateButtonProps {
  items: Item[]
  onFloatingActionButtonItemPress: (name: string) => void
  allowCreateTypes: ConversationType[] | undefined
  onCreateConversation: (conversationType: ConversationType) => void
  chatEnabled: boolean | undefined
  companyId?: Id
}

function GetCreateButton({
  items,
  onFloatingActionButtonItemPress,
  allowCreateTypes,
  onCreateConversation,
  chatEnabled,
  companyId,
}: GetCreateButtonProps) {
  const allowSendMessage = allowCreateTypes?.includes('dm')
  const allowCreateGroup = allowCreateTypes?.includes('custom')

  if (!allowCreateGroup && !allowSendMessage) {
    return <></>
  }

  if (!allowCreateGroup || !chatEnabled) {
    return (
      <FloatingActionButton
        key={`fab-${companyId}`}
        icon="envelope"
        onPress={() => onCreateConversation('dm')}
      />
    )
  }

  return (
    <FloatingActionButton
      key={`fab-${companyId}-${items.length}`}
      items={items}
      onPressItem={onFloatingActionButtonItemPress}
      useBottomInset={false}
    />
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
}))
